import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { DownArrow } from "../../components/icons";
import { apiGetStandingMetrics } from "../../api/backend/brandAmbassador";
import {
  METRIC_IDS,
  metricStandingMessages,
} from "../../constants/brandAmbassador";
import "./standingMetricsContract.scss";

// map your metricKeys → DB columns + front-end fields
const fieldMapping = {
  BE_ON_TIME: { recordField: "onTimeIssueCount", configField: "issues" },
  MEET_DAILY_GOALS: {
    recordField: "dailyGoalIssueCount",
    configField: "dailyGoalIssueCount",
  },
  BE_RESPECTFUL: {
    recordField: "respectfulIssueCount",
    configField: "respectfulIssueCount",
  },
  NEAT_CLEAN_ATTIRE: {
    recordField: "cleanAppearanceIssueCount",
    configField: "cleanAppearanceIssueCount",
  },
  RESPONSIBLE_CANCELATION: {
    recordField: "cancelationIssueCount",
    configField: "cancelationIssueCount",
  },
};

export default function StandingMetricsContract() {
  const { userId } = useParams();
  const [openMetrics, setOpenMetrics] = useState({});
  const [rawHistory, setRawHistory] = useState([]);

  // ← this is your fetch
  useEffect(() => {
    apiGetStandingMetrics(userId)
      .then((res) => setRawHistory(res.data.standingHistory))
      .catch(() => setRawHistory([]));
  }, [userId]);

  // pivot into exactly one record per metricKey
  const displayMetricsData = useMemo(() => {
    // start with a default zero bucket
    return Object.keys(METRIC_IDS).map((metricKey) => {
      const { recordField } = fieldMapping[metricKey];
      const found = rawHistory.find((r) => r.standingMetricsName === metricKey);
      return {
        standingMetricsName: metricKey,
        [recordField]: found ? found[recordField] : 0,
        issues: [], // always empty with the new backend
      };
    });
  }, [rawHistory]);

  const toggleMetric = (i) => setOpenMetrics((o) => ({ ...o, [i]: !o[i] }));

  return (
    <div className="standing-metrics-contract-container">
      <h2>Standing Metrics Contract</h2>
      <p>
        To maintain your current standing, please adhere to the following
        requirements:
      </p>
      <ul>
        {displayMetricsData.map((record, idx) => {
          const key = record.standingMetricsName;
          const metricId = METRIC_IDS[key];
          const config = metricStandingMessages[metricId];
          const mapping = fieldMapping[key];

          const issueCount = record[mapping.recordField];
          const messages = config[mapping.configField];
          const defined = Object.keys(messages).map(Number);
          const maxDefined = Math.max(...defined);
          const displayCnt =
            issueCount > maxDefined ? `${maxDefined}+` : issueCount;
          const message = messages[Math.min(issueCount, maxDefined)];

          return (
            <li key={key} className="metric-item">
              <div className="metric-icon-wrapper">{config.icon}</div>
              <div
                className="metric-text-row"
                onClick={() => toggleMetric(idx)}
              >
                <span className="metric-name">{config.name}</span>
                {issueCount > 0 && (
                  <span className="issueCountAndArrow">
                    ({displayCnt} {issueCount === 1 ? "issue" : "issues"})
                    {/* ONLY this span gets the arrow class! */}
                    <span className={`arrow ${openMetrics[idx] ? "open" : ""}`}>
                      <DownArrow
                        width="17.6"
                        height="13.9"
                        fillColor="var(--color-bloodred)"
                      />
                    </span>
                  </span>
                )}
              </div>

              {openMetrics[idx] ? (
                record.issues.length > 0 ? (
                  <ul className="issuesList">
                    {record.issues.map((iss, i) => (
                      <li key={i}>
                        <strong>{iss.date}:</strong> {iss.remedy}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="no-issues">{message}</p>
                )
              ) : null}
            </li>
          );
        })}
      </ul>
      <p className="disclaimer">
        Note: Failure to meet these metrics may result in a reduction of your
        standing status.{" "}
        <HashLink to="/terms-of-service" className="terms-link">
          Terms of Service
        </HashLink>
      </p>
    </div>
  );
}
